import * as THREE from "three";
import { CHUNK_SIZE, SEA_LEVEL, SUN_DIRECTION, VOXELS_PER_METRE, WORLD_EXTENT } from "@sim/world/constants";
import { VoxelWorld } from "@sim/world/world";
import { BlockMaterialSet, meshDefinitionsFromRegistry } from "@render/materials/BlockMaterial";
import { ChunkStreamer } from "@render/streaming/ChunkStreamer";
import { WORLDGEN_BLOCKS, WorldgenChunkLoader, alphaRegionGraph, terrainHeight } from "@worldgen/index";
import { MeshWorkerPool } from "./workers/MeshWorkerPool";
import { Sky } from "@render/Sky";
import { createBody, eyePosition, PLAYER_EYE_HEIGHT, stepBody, type MoveInput } from "@sim/physics/body";

const WORLD_SEED = 0x51be_2026;

/**
 * Scene units are voxels, and there are eight to the metre. Everything with a
 * physical meaning is written in metres and converted here, once — the numbers
 * in this file were authored when a unit was a metre, so leaving any of them
 * bare puts it an eighth of where it belongs (a spawn at y=104 was above the
 * treetops and is now forty metres underground).
 */
function m(metres: number): number {
  return metres * VOXELS_PER_METRE;
}

const query = new URLSearchParams(location.search);

function queryNumber(name: string, fallback: number): number {
  const raw = query.get(name);
  if (raw === null) return fallback;
  const value = Number(raw);
  return Number.isFinite(value) && value > 0 ? value : fallback;
}

/**
 * Horizontal streaming reach. One chunk is 32 voxels, so four metres.
 *
 * Overridable from the URL because the right value is a memory and frame-time
 * question that has to be measured against a real GPU, not reasoned about: at
 * eight voxels per metre a chunk holds far more geometry than it did at one.
 */
const STREAM_RADIUS = queryNumber("radius", 12);
/**
 * Vertical reach is deliberately shorter than the horizontal. The resident set
 * is a sphere by default, and a full sphere at radius 12 is ~7,200 sub-chunks;
 * clamping to +-4 brings it to ~3,900, which is what the engine carried before
 * the rescale. Sixteen metres up and down covers surface relief, and the window
 * follows the player down into caves.
 */
const STREAM_VERTICAL_RADIUS = queryNumber("verticalRadius", 6);

/**
 * Fog has to close before the streaming radius does, or the world visibly ends
 * at a hard edge — but the job it is actually doing is aerial perspective, and
 * that is a *mid-range* effect.
 *
 * An exp2 curve is deliberately flat near the viewer, so at 0.0022 the fog did
 * essentially nothing until the streaming edge: 15% at twenty metres, and hills
 * met the sky at full contrast and full saturation. The references lose contrast
 * steadily with distance, which is what separates their near grass from their
 * far treeline.
 *
 * Solved against the combined near/horizon blend in BlockMaterial rather than
 * picked: this gives ~2% at 8 m, 18% at 20 m, 43% at 32 m and 71% at the radius,
 * so the world fades out well before it runs out.
 *
 * 0.0030 was tried first and overshot. Fog is a *blend toward the sky*, so every
 * point of it is a point of chroma off the terrain, and at 0.0030 the mid-ground
 * lost enough saturation to drag a whole meadow from 48% down to 35% against the
 * references' 60%. The references do lose contrast with distance, but toward a
 * dark, still-green haze rather than a pale blue veil.
 */
const FOG_DENSITY = 0.0024;
/**
 * Horizon sky, and therefore the colour distance fades into.
 *
 * Deliberately a washed-out blue-grey rather than the 0xa6c4dd it was: this is
 * the colour every distant surface is being mixed toward, so its saturation is
 * spent directly out of theirs. Pulling 12 points of saturation out of the haze
 * costs the sky very little — it still reads as sky, against a zenith that is
 * still properly blue — and hands all of it back to the land.
 */
const SKY_COLOR = new THREE.Color(0xb2c2c8);

const boot = document.querySelector<HTMLElement>("#boot");
let bootFailure: string | null = null;

function failureMessage(reason: unknown): string {
  if (reason instanceof Error) return reason.message;
  if (typeof reason === "string") return reason;
  return "unknown startup failure";
}

function surfaceBootFailure(reason: unknown): void {
  const message = failureMessage(reason);
  bootFailure = `World failed to start · ${message}`;
  document.documentElement.dataset["boot"] = "failed";
  document.documentElement.dataset["bootError"] = message;
  if (boot) {
    boot.textContent = bootFailure;
    boot.style.color = "#fff2e8";
    boot.style.borderColor = "rgb(255 117 84 / 78%)";
    boot.style.background = "rgb(91 17 10 / 92%)";
    boot.style.whiteSpace = "normal";
    boot.style.maxWidth = "min(760px, calc(100vw - 48px))";
  }
  console.error("Vibe World startup failure", reason);
}

window.addEventListener("error", (event) => surfaceBootFailure(event.error ?? event.message));
window.addEventListener("unhandledrejection", (event) => surfaceBootFailure(event.reason));
const canvas = document.querySelector<HTMLCanvasElement>("#game");
if (!canvas) {
  const error = new Error("missing #game canvas");
  surfaceBootFailure(error);
  throw error;
}
if (boot) boot.textContent = "Starting…";

const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, powerPreference: "high-performance" });
renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 0.95;

const scene = new THREE.Scene();
// No flat background: the sky is a dome, and the fog blends into its horizon
// colour so distant terrain sits on a colour the sky actually has.
const sky = new Sky((STREAM_RADIUS + 1) * CHUNK_SIZE);
scene.add(sky.mesh);
// Keep Three's scene fog in sync for future non-block materials. BlockMaterial
// performs the same exp2 blend itself because it is a RawShaderMaterial.
scene.fog = new THREE.FogExp2(SKY_COLOR, FOG_DENSITY);

// Far plane sits just past the streaming radius; there is nothing resident
// beyond it to draw. Near plane is 2.5 cm, well inside one voxel.
const camera = new THREE.PerspectiveCamera(70, 1, 0.2, (STREAM_RADIUS + 2) * CHUNK_SIZE);
camera.rotation.order = "YXZ";
let yaw = -Math.PI * 0.5;
let pitch = -0.28;

const world = new VoxelWorld(WORLD_SEED, WORLDGEN_BLOCKS);
const materials = new BlockMaterialSet();
// Ambient is the sky, and in the references the sky is doing most of the work:
// no face there reads as black, and vertical surfaces stay clearly coloured.
// A dim ambient plus a strong sun gives the opposite — every side face collapses
// to near-black and the eroded ground looks punched full of holes.
// Softer than it was. The references read as overcast-bright: the sky carries
// most of the illumination, the sun adds warmth and direction rather than a
// hard key, and nothing is blown out. A strong sun against a weak ambient gives
// the plastic look — flat bright tops, dead sides, no middle.
//
// The ambient is near-neutral rather than sky-blue on purpose. A blue ambient is
// the physically honest choice and it was wrong here: it multiplies every
// albedo, so it rotated the whole world's hue toward cyan and pushed bare stone
// to a rendered hue of 216 — lavender rock. In the references, shade is the same
// hue as sun, several stops down: their shaded grass measures hue 66 against
// their sunlit grass at 65. Form still comes from the hemisphere, which is a
// *value* gradient from zenith to bounce, not a colour one.
materials.setSun(
  new THREE.Vector3(SUN_DIRECTION.x, SUN_DIRECTION.y, SUN_DIRECTION.z).normalize(),
  new THREE.Color(0xffeeda),
  // Sky overhead, bounced ground light underneath. The bounce carries most of
  // the light on vertical faces, so it is far brighter than it looks it should
  // be: at 0x4a4034 a shaded side face landed at 9% value against the
  // references' 18-25%.
  //
  // And it is olive, not grey. A *neutral* bounce bright enough to lift the
  // shade adds the same amount to all three channels, which desaturates
  // everything it lifts — brightening the shadows cost 6 points of saturation
  // and the world went chalky. Light bouncing off a forest floor is green, so
  // tinting the bounce toward the foliage lifts the green channel hardest and
  // buys the value back without spending the chroma.
  new THREE.Color(0xd2d6d1),
  new THREE.Color(0x838c6d),
);
// The second density is the denser blend the cutout groups use; keep the ratio
// the art direction was tuned at rather than restating both numbers.
materials.setFog(SKY_COLOR, FOG_DENSITY, FOG_DENSITY * 1.75);
sky.setSun(new THREE.Vector3(SUN_DIRECTION.x, SUN_DIRECTION.y, SUN_DIRECTION.z).normalize(), new THREE.Color(0xffeeda));
sky.setColors(SKY_COLOR, new THREE.Color(0x5f93c6));
const meshWorkers = new MeshWorkerPool();
const worldgenWorkers = new WorldgenChunkLoader(WORLD_SEED, undefined, 4);
const streamer = new ChunkStreamer(world, WORLDGEN_BLOCKS, scene, materials, meshWorkers, worldgenWorkers, {
  radius: STREAM_RADIUS,
  verticalRadius: STREAM_VERTICAL_RADIUS,
  loadsPerFrame: 12,
  meshesPerFrame: 3,
  uploadsPerFrame: 4,
  uploadBudgetMs: 3,
  definitions: meshDefinitionsFromRegistry(WORLDGEN_BLOCKS),
  // The streamer only reports unrecovered failures — permanent rejections or
  // transient retries that exhausted their budget. Recovered transient errors
  // never reach this handler, so they cannot leave a sticky capture-blocking
  // bootError behind.
  onError(error, stage, position): void {
    surfaceBootFailure(new Error(`${stage} failed at ${position.cx},${position.cy},${position.cz}: ${error.message}`));
  },
});

const keys = new Set<string>();
window.addEventListener("keydown", (event) => keys.add(event.code));
window.addEventListener("keyup", (event) => keys.delete(event.code));
canvas.addEventListener("click", () => void canvas.requestPointerLock());
window.addEventListener("mousemove", (event) => {
  if (document.pointerLockElement !== canvas) return;
  yaw -= event.movementX * 0.002;
  pitch = THREE.MathUtils.clamp(pitch - event.movementY * 0.002, -Math.PI * 0.49, Math.PI * 0.49);
});

function resize(): void {
  const width = innerWidth;
  const height = innerHeight;
  renderer.setSize(width, height, false);
  camera.aspect = width / Math.max(1, height);
  camera.updateProjectionMatrix();
}
window.addEventListener("resize", resize);
resize();

interface CameraPose {
  readonly label: "coast" | "forest" | "plains";
  readonly position: THREE.Vector3;
  readonly target: THREE.Vector3;
}

const graph = alphaRegionGraph(WORLD_SEED);
const forestSite = graph.regions.find((region) => region.biome === "forest")?.sites[0];
const plainsSite = graph.regions.find((region) => region.biome === "plains")?.sites[0];

// Spawn on the ground at the middle of the map rather than at a fixed height:
// terrain now sits around y=512, so any hardcoded altitude is either buried or
// in the stratosphere.
/**
 * `?at=x,z` drops the player anywhere in the world.
 *
 * Terrain features worth looking at — a river, a rock face — are somewhere
 * specific, and hunting for them by walking is not a thing a headless capture
 * can do.
 */
function queryPoint(name: string, fallbackX: number, fallbackZ: number): readonly [number, number] {
  const raw = query.get(name);
  if (raw === null) return [fallbackX, fallbackZ];
  const [rawX, rawZ] = raw.split(",");
  const x = Number(rawX);
  const z = Number(rawZ);
  if (!Number.isFinite(x) || !Number.isFinite(z)) return [fallbackX, fallbackZ];
  return [Math.round(x), Math.round(z)];
}

const [spawnX, spawnZ] = queryPoint("at", Math.round(WORLD_EXTENT * 0.5), Math.round(WORLD_EXTENT * 0.5));
// A little clearance above the smooth height field: the surface carries up to
// SURFACE_EROSION voxels of bumpiness that terrainHeight deliberately excludes,
// so spawning exactly on it can start the player inside a mound.
const player = createBody({
  x: spawnX + 0.5,
  y: terrainHeight(WORLD_SEED, spawnX, spawnZ, graph) + m(0.5),
  z: spawnZ + 0.5,
});
// `?h=` lifts the eye, `?look=x,z` aims at a point. Between them a headless
// capture can frame a feature without a human at the keyboard.
player.position.y += queryNumber("h", 0);
const eyeSpawn = eyePosition(player);
camera.position.set(eyeSpawn.x, eyeSpawn.y, eyeSpawn.z);
const lookTarget = query.get("look");
if (lookTarget !== null) {
  const [lookX, lookZ] = queryPoint("look", spawnX, spawnZ);
  const groundY = terrainHeight(WORLD_SEED, lookX, lookZ, graph);
  const direction = new THREE.Vector3(lookX - camera.position.x, groundY - camera.position.y, lookZ - camera.position.z)
    .normalize();
  yaw = Math.atan2(-direction.x, -direction.z);
  pitch = Math.asin(THREE.MathUtils.clamp(direction.y, -1, 1));
}

/** Fixed physics step, and the most simulated time one frame may catch up. */
const PHYSICS_STEP = 1 / 120;
const MAX_PHYSICS_BACKLOG = 0.25;
let physicsBacklog = 0;
// Fly mode stays available for inspecting the world and for the capture poses.
// `?fly=1` starts in it, so a raised `?h=` viewpoint is not immediately pulled
// back to the ground by gravity before the capture lands.
let flying = query.get("fly") === "1";
window.addEventListener("keydown", (event) => {
  if (event.code === "KeyF") flying = !flying;
});

function landscapePose(
  label: CameraPose["label"],
  targetX: number,
  targetZ: number,
  offsetX: number,
  offsetZ: number,
): CameraPose {
  const targetY = terrainHeight(WORLD_SEED, targetX, targetZ, graph) + m(4);
  return {
    label,
    position: new THREE.Vector3(
      targetX + offsetX,
      terrainHeight(WORLD_SEED, targetX + offsetX, targetZ + offsetZ, graph) + m(12),
      targetZ + offsetZ,
    ),
    target: new THREE.Vector3(targetX, targetY, targetZ),
  };
}

const benchmarkPoses: readonly CameraPose[] = [
  {
    label: "coast",
    // Just inland of the shelf, looking out over the water at the world edge.
    position: new THREE.Vector3(m(90), SEA_LEVEL + m(9), WORLD_EXTENT * 0.5 + m(18)),
    target: new THREE.Vector3(m(30), SEA_LEVEL - m(2), WORLD_EXTENT * 0.5),
  },
  landscapePose("forest", forestSite?.x ?? m(430), forestSite?.z ?? m(430), -m(18), m(15)),
  landscapePose("plains", plainsSite?.x ?? m(650), plainsSite?.z ?? m(590), -m(20), m(14)),
];

function orientToward(target: THREE.Vector3): void {
  const direction = target.clone().sub(camera.position).normalize();
  yaw = Math.atan2(-direction.x, -direction.z);
  pitch = Math.asin(THREE.MathUtils.clamp(direction.y, -1, 1));
  camera.rotation.set(pitch, yaw, 0);
}

let benchmarkView: number | null = null;
let capturePaused = false;
function applyBenchmarkView(index: number): void {
  const pose = benchmarkPoses[index];
  if (!pose) throw new RangeError(`unknown benchmark view ${index}`);
  camera.position.copy(pose.position);
  orientToward(pose.target);
}

const traceMode = query.has("trace");
const traceDurationMs = Math.max(1_000, Number(query.get("duration") ?? 30_000));
const frameTimes: number[] = [];
const clock = new THREE.Clock();
let elapsedMs = 0;
let lastHudUpdate = 0;
let lastStats = streamer.update(camera);
let traceComplete = false;

interface TraceResult {
  readonly frames: number;
  readonly durationMs: number;
  readonly radius: number;
  readonly frameTimes: readonly number[];
  readonly min: number;
  readonly median: number;
  readonly p95: number;
  readonly maxLoadedChunks: number;
  readonly finalLoadedChunks: number;
}

interface AmbientOcclusionComparison {
  readonly meanChannelDarkening: number;
  readonly meanDarkerChannelDelta: number;
  readonly changedPixelRatio: number;
  readonly darkerPixelRatio: number;
  readonly stronglyDarkerPixelRatio: number;
  readonly sampledPixels: number;
}

let maximumLoadedChunks = 0;

function percentile(sorted: readonly number[], fraction: number): number {
  return sorted[Math.min(sorted.length - 1, Math.floor((sorted.length - 1) * fraction))] ?? 0;
}

function finishTrace(): void {
  if (traceComplete || frameTimes.length === 0) return;
  traceComplete = true;
  elapsedMs = Math.max(elapsedMs, traceDurationMs);
  benchmarkView = null;
  moveCamera(0);
  camera.rotation.set(pitch, yaw, 0);
  lastStats = streamer.update(camera);
  maximumLoadedChunks = Math.max(maximumLoadedChunks, world.loadedChunkCount);
  const sorted = [...frameTimes].sort((a, b) => a - b);
  const result: TraceResult = {
    frames: sorted.length,
    durationMs: traceDurationMs,
    radius: STREAM_RADIUS,
    frameTimes: [...frameTimes],
    min: percentile(sorted, 0),
    median: percentile(sorted, 0.5),
    p95: percentile(sorted, 0.95),
    maxLoadedChunks: maximumLoadedChunks,
    finalLoadedChunks: world.loadedChunkCount,
  };
  (window as unknown as { __VOXEL_TRACE__: TraceResult }).__VOXEL_TRACE__ = result;
  document.documentElement.dataset["trace"] = "complete";
  document.documentElement.dataset["traceResult"] = JSON.stringify(result);
  console.info(
    `[voxel-trace] frames=${result.frames} min=${result.min.toFixed(2)}ms median=${result.median.toFixed(2)}ms `
      + `p95=${result.p95.toFixed(2)}ms loaded-max=${result.maxLoadedChunks} loaded-final=${result.finalLoadedChunks}`,
  );
}

if (traceMode) window.setTimeout(finishTrace, traceDurationMs + 100);

const flightCurve = new THREE.CatmullRomCurve3(benchmarkPoses.map((pose) => pose.position), false, "catmullrom", 0.25);

function moveCamera(delta: number): void {
  if (benchmarkView !== null) {
    applyBenchmarkView(benchmarkView);
    return;
  }
  if (traceMode) {
    const progress = Math.min(1, elapsedMs / traceDurationMs);
    if (progress >= 1) {
      const outsideWorld = (STREAM_RADIUS + 2) * CHUNK_SIZE;
      camera.position.set(WORLD_EXTENT + outsideWorld, SEA_LEVEL, WORLD_EXTENT * 0.5);
      yaw = -Math.PI * 0.5;
      pitch = -0.2;
      return;
    }
    camera.position.copy(flightCurve.getPoint(progress));
    const tangent = flightCurve.getTangent(Math.min(0.999, progress + 0.002));
    yaw = Math.atan2(-tangent.x, -tangent.z);
    pitch = -0.25;
    return;
  }

  if (flying) {
    const speed = (keys.has("ShiftLeft") || keys.has("ShiftRight")) ? m(14) : m(5.6);
    const forward = new THREE.Vector3(-Math.sin(yaw), 0, -Math.cos(yaw));
    const right = new THREE.Vector3(Math.cos(yaw), 0, -Math.sin(yaw));
    if (keys.has("KeyW")) camera.position.addScaledVector(forward, speed * delta);
    if (keys.has("KeyS")) camera.position.addScaledVector(forward, -speed * delta);
    if (keys.has("KeyD")) camera.position.addScaledVector(right, speed * delta);
    if (keys.has("KeyA")) camera.position.addScaledVector(right, -speed * delta);
    if (keys.has("Space")) camera.position.y += speed * delta;
    if (keys.has("ControlLeft") || keys.has("ControlRight")) camera.position.y -= speed * delta;
    // Keep the body under the camera so dropping out of fly mode does not
    // teleport the player back to where they took off.
    player.position.x = camera.position.x;
    player.position.y = camera.position.y - PLAYER_EYE_HEIGHT;
    player.position.z = camera.position.z;
    player.velocity.x = 0;
    player.velocity.y = 0;
    player.velocity.z = 0;
    return;
  }

  const input: MoveInput = {
    forward: (keys.has("KeyW") ? 1 : 0) - (keys.has("KeyS") ? 1 : 0),
    strafe: (keys.has("KeyD") ? 1 : 0) - (keys.has("KeyA") ? 1 : 0),
    jump: keys.has("Space"),
    sprint: keys.has("ShiftLeft") || keys.has("ShiftRight"),
    yaw,
  };
  // Physics resolves collision against the post-move state rather than sweeping
  // through it, so the step has to stay small however long the frame was.
  physicsBacklog = Math.min(physicsBacklog + delta, MAX_PHYSICS_BACKLOG);
  while (physicsBacklog >= PHYSICS_STEP) {
    stepBody(world, player, input, PHYSICS_STEP);
    physicsBacklog -= PHYSICS_STEP;
  }
  const eye = eyePosition(player);
  camera.position.set(eye.x, eye.y, eye.z);
}

function pixels(): Uint8Array {
  renderer.render(scene, camera);
  const width = renderer.domElement.width;
  const height = renderer.domElement.height;
  const data = new Uint8Array(width * height * 4);
  renderer.getContext().readPixels(0, 0, width, height, renderer.getContext().RGBA, renderer.getContext().UNSIGNED_BYTE, data);
  return data;
}

function compareAmbientOcclusion(): AmbientOcclusionComparison {
  materials.setAmbientOcclusion(false);
  const flat = pixels();
  materials.setAmbientOcclusion(true);
  const shaded = pixels();
  let changed = 0;
  let darker = 0;
  let darkening = 0;
  let darkerChannelDelta = 0;
  let stronglyDarker = 0;
  let sampled = 0;
  // Sample every fourth physical pixel; the comparison remains deterministic
  // while avoiding a long synchronous benchmark-side scan at high DPI.
  for (let offset = 0; offset < shaded.length; offset += 16) {
    const flatLuma = (flat[offset] ?? 0) + (flat[offset + 1] ?? 0) + (flat[offset + 2] ?? 0);
    const shadedLuma = (shaded[offset] ?? 0) + (shaded[offset + 1] ?? 0) + (shaded[offset + 2] ?? 0);
    const delta = flatLuma - shadedLuma;
    if (Math.abs(delta) >= 2) changed += 1;
    if (delta >= 2) {
      darker += 1;
      darkerChannelDelta += delta / 3;
    }
    if (delta >= 36) stronglyDarker += 1;
    darkening += delta / 3;
    sampled += 1;
  }
  return {
    meanChannelDarkening: darkening / Math.max(1, sampled),
    meanDarkerChannelDelta: darkerChannelDelta / Math.max(1, darker),
    changedPixelRatio: changed / Math.max(1, sampled),
    darkerPixelRatio: darker / Math.max(1, sampled),
    stronglyDarkerPixelRatio: stronglyDarker / Math.max(1, sampled),
    sampledPixels: sampled,
  };
}

const debugRenderer = renderer.getContext().getExtension("WEBGL_debug_renderer_info");
const gpuRenderer = debugRenderer
  ? String(renderer.getContext().getParameter(debugRenderer.UNMASKED_RENDERER_WEBGL))
  : "";

(window as unknown as {
  __VOXEL_BENCHMARK__: {
    readonly views: readonly string[];
    readonly gpuRenderer: string;
    setView(index: number): void;
    setAmbientOcclusion(enabled: boolean): void;
    setCapturePaused(paused: boolean): void;
    compareAmbientOcclusion(): AmbientOcclusionComparison;
    state(): object;
  };
}).__VOXEL_BENCHMARK__ = {
  views: benchmarkPoses.map((pose) => pose.label),
  gpuRenderer,
  setView(index: number): void {
    benchmarkView = index;
    applyBenchmarkView(index);
    lastStats = streamer.update(camera);
  },
  setAmbientOcclusion(enabled: boolean): void {
    materials.setAmbientOcclusion(enabled);
    renderer.render(scene, camera);
  },
  setCapturePaused(paused: boolean): void {
    capturePaused = paused;
  },
  compareAmbientOcclusion,
  state(): object {
    // Recompute visibility for the current camera so readiness never reads a
    // stale frame's culling stats (e.g. while streaming updates are paused).
    const stats = streamer.visibility(camera);
    return {
      loaded: world.loadedChunkCount,
      desired: streamer.desiredCount,
      loading: streamer.loadingCount,
      queuedLoads: streamer.queuedLoads,
      queuedMeshes: streamer.queuedMeshes,
      queuedUploads: streamer.queuedUploads,
      activeMeshes: meshWorkers.pending,
      visible: stats.visibleChunks,
      visibleDrawCalls: stats.visibleDrawCalls,
      view: benchmarkView,
      bootError: bootFailure,
    };
  },
};

function frame(): void {
  requestAnimationFrame(frame);
  const rawDelta = clock.getDelta();
  const delta = Math.min(rawDelta, 0.1);
  elapsedMs += rawDelta * 1_000;
  if (traceMode && !traceComplete && rawDelta > 0 && elapsedMs > 250) frameTimes.push(rawDelta * 1_000);
  moveCamera(delta);
  camera.rotation.set(pitch, yaw, 0);
  materials.setTime(elapsedMs / 1000);
  sky.setTime(elapsedMs / 1000);
  sky.follow(camera);
  if (!capturePaused) lastStats = streamer.update(camera);
  maximumLoadedChunks = Math.max(maximumLoadedChunks, world.loadedChunkCount);
  renderer.render(scene, camera);
  if (traceMode && elapsedMs >= traceDurationMs) finishTrace();

  if (boot && elapsedMs - lastHudUpdate >= 250) {
    lastHudUpdate = elapsedMs;
    if (bootFailure) boot.textContent = bootFailure;
    else {
      // The streaming counters are on the HUD because they are the first thing
      // worth knowing when the world comes up empty, and a headless screenshot
      // can read them without a debugger attached.
      const pipeline = `want ${streamer.desiredCount} · have ${world.loadedChunkCount}`
        + ` · loading ${streamer.loadingCount} · q ${streamer.queuedLoads}/${streamer.queuedMeshes}/${streamer.queuedUploads}`
        + ` · vis ${lastStats.visibleChunks}`;
      boot.textContent = traceMode
        ? `GPU flight · ${Math.min(100, elapsedMs / traceDurationMs * 100).toFixed(0)}% · ${pipeline}`
        : `Vibe World · ${flying ? "flying" : "walking"} (F) · ${pipeline}`;
    }
  }
}
requestAnimationFrame(frame);

window.addEventListener("beforeunload", () => {
  streamer.dispose();
  sky.dispose();
  worldgenWorkers.terminate();
  meshWorkers.terminate();
  materials.dispose();
  renderer.dispose();
});
